import { GeoJsonLayer } from '@deck.gl/layers';
import proj4 from 'proj4';
import bbox from '@turf/bbox';
import { showMapConfirmToast } from "../utils/Toast/mapToast";

const extractEPSGCode = (srsName) => {
    const match = srsName.match(/EPSG::(\d+)/) || srsName.match(/EPSG:(\d+)/);
    return match ? `EPSG:${match[1]}` : null;
};


const transformCoordinates = (coords, sourceCRS, targetCRS) => {
    return coords.map(coord => {
        try {
            const [x, y] = coord;
            const [lon, lat] = proj4(sourceCRS, targetCRS, [x, y]);
            return [lon, lat];
        } catch (error) {
            console.error('Error transforming coordinates:', error);
            return coord;
        }
    });
};

const transformGeoJSONCoordinates = (geometry, sourceCRS, targetCRS) => {
    const { type, coordinates } = geometry;

    switch (type) {
        case 'Point':
            return {
                ...geometry,
                coordinates: transformCoordinates([coordinates], sourceCRS, targetCRS)[0],
            };
        case 'LineString':
            return {
                ...geometry,
                coordinates: transformCoordinates(coordinates, sourceCRS, targetCRS),
            };
        case 'Polygon':
            return {
                ...geometry,
                coordinates: coordinates.map(ring => transformCoordinates(ring, sourceCRS, targetCRS)),
            };
        case 'MultiPoint':
            return {
                ...geometry,
                coordinates: transformCoordinates(coordinates, sourceCRS, targetCRS),
            };
        case 'MultiLineString':
            return {
                ...geometry,
                coordinates: coordinates.map(line => transformCoordinates(line, sourceCRS, targetCRS)),
            };
        case 'MultiPolygon':
            return {
                ...geometry,
                coordinates: coordinates.map(polygon =>
                    polygon.map(ring => transformCoordinates(ring, sourceCRS, targetCRS))
                ),
            };
        default:
            console.error('Unsupported geometry type:', type);
            return geometry;
    }
};

const transformGeoJSON = (geojson, sourceCRS, targetCRS = 'EPSG:4326') => {
    return {
        ...geojson,
        features: geojson.features.map(feature => ({
            ...feature,
            geometry: transformGeoJSONCoordinates(feature.geometry, sourceCRS, targetCRS),
        })),
    };
};

const handleFileOpen = async (file) => {
    if (!file) throw new Error("No file provided");

    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            try {
                const geojson = JSON.parse(reader.result);

                if (!geojson?.features || !Array.isArray(geojson.features)) {
                    reject(new Error("Fajl nije ispravan GeoJSON FeatureCollection"));
                    return;
                }

                const cleanGeojson = {
                    type: "FeatureCollection",
                    features: geojson.features,
                };

                resolve(cleanGeojson);
            } catch (error) {
                console.error("Failed to parse GeoJSON:", error);
                reject(new Error("Failed to parse GeoJSON"));
            }
        };

        reader.onerror = () => {
            reject(new Error("Failed to read file"));
        };

        reader.readAsText(file);
    });
};


export const loadGeoJSON = async (file) => {
    try {
        const convertedGeojson = await handleFileOpen(file);
        return convertedGeojson;
    } catch (error) {
        console.error('Error loading GeoJSON:', error);
        throw error;
    }
};

export const setGeoJSONLayer = (layerIndex, geojson, setHoverInfo, setWfsLayers, setViewState) => {
    setWfsLayers(prev => [
        ...prev,
        new GeoJsonLayer({
            id: `gj-${layerIndex}`,
            data: geojson,
            source: "catalog",
            opacity: 1,
            pickable: true,
            stroked: true,
            filled: true,
            visible: true,
            pointType: 'circle',
            lineWidthMinPixels: 2,
            lineWidthScale: 2,
            getFillColor: [255, 0, 0, 100],
            getLineColor: [255, 0, 0, 200],
            getPointRadius: 2,
            getLineWidth: 2,
            onHover: info => setHoverInfo(info),
            onError: (error) => console.error('GeoJsonLayer error:', error),
        }),
    ]);

    if (geojson && geojson.features && geojson.features.length > 0) {
        const [minX, minY, maxX, maxY] = bbox(geojson);
        const centerLon = (minX + maxX) / 2;
        const centerLat = (minY + maxY) / 2;
        const lonRange = maxX - minX;
        const latRange = maxY - minY;
        const zoom = Math.log2(360 / Math.max(lonRange, latRange));

        showMapConfirmToast({
            title: "Pozicioniranje mape",
            message: "Da li želite da se prikaz mape pozicionira prema učitanom GeoJSON sloju?",
            confirmText: "Da",
            cancelText: "Ne",
            variant: "info",
            onConfirm: () => {
                setViewState(prev => ({
                    ...prev,
                    longitude: centerLon,
                    latitude: centerLat,
                    zoom,
                    pitch: 0,
                    bearing: 0,
                    transitionDuration: 1000,
                }));
            },
        });
    }
};